/**
 * M8 ekranlarının ortak kabuk parçaları.
 *
 * Burada YENİ bir tasarım dili üretilmez; @kantin/ui bileşenleri sarmalanır.
 * Tek amaç, tasarım sisteminin "her liste/kart için YÜKLENİYOR, BOŞ ve HATA
 * durumu YAZILIR" kuralını bir alışkanlığa değil, bir bileşene bağlamaktır.
 */
import type { ReactNode, TextareaHTMLAttributes } from "react";
import { forwardRef, useId, useState } from "react";
import { BosDurum, Buton, Iskelet, Rozet, Uyari } from "@kantin/ui";
import type { SorguParam } from "./veri/api.js";
import { ApiHatasi, raporIndir, yetkisizMi } from "./veri/api.js";

// ---------------------------------------------------------------------------
// Hata metni
// ---------------------------------------------------------------------------

export function hataMetni(e: unknown): string {
  if (e instanceof ApiHatasi) return e.message;
  if (e instanceof Error) return e.message;
  return "Beklenmeyen bir hata oluştu.";
}

// ---------------------------------------------------------------------------
// Sayfa kabuğu
// ---------------------------------------------------------------------------

export function Sayfa({ children }: { children: ReactNode }) {
  /* Tüm raporlar aynı boşlukları ve içerik genişliğini paylaşır. */
  return (
    <div className="yonetim-sayfa flex flex-col gap-5 p-4 md:p-8">
      {children}
    </div>
  );
}

export function SayfaBasligi({
  baslik,
  aciklama,
  eylem,
}: {
  baslik: string;
  aciklama?: ReactNode;
  eylem?: ReactNode;
}) {
  /* Başlık ve açıklama solda, sayfanın eylemleri sağda durur. */
  return (
    <header className="yonetim-sayfa-basligi flex flex-wrap items-center justify-between gap-4">
      <div className="min-w-0 max-w-icerik">
        <h1 className="m-0 text-2xl">{baslik}</h1>
        {aciklama && (
          <p className="m-0 mt-1 text-sm text-murekkep-2">{aciklama}</p>
        )}
      </div>
      {eylem && (
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          {eylem}
        </div>
      )}
    </header>
  );
}

/** Filtre şeridi — ekranların üst kısmındaki seçicileri hizalar. */
export function FiltreSeridi({ children }: { children: ReactNode }) {
  return (
    <div className="yonetim-filtre flex flex-wrap items-end gap-3 rounded-lg border border-cetvel bg-yuzey p-4 print:hidden">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Yükleniyor / hata / boş — üçü de HER liste için zorunlu
// ---------------------------------------------------------------------------

export function SorguDurumu({
  yukleniyor,
  hata,
  bos,
  bosBaslik = "Kayıt yok",
  bosAciklama,
  bosEylem,
  iskeletSatir = 5,
  yenile,
  children,
}: {
  yukleniyor: boolean;
  hata: unknown;
  bos: boolean;
  bosBaslik?: string;
  bosAciklama?: string;
  bosEylem?: ReactNode;
  iskeletSatir?: number;
  yenile?: () => void;
  children: ReactNode;
}) {
  if (yukleniyor) {
    return (
      <div className="p-4">
        <Iskelet satir={iskeletSatir} />
      </div>
    );
  }
  if (hata) {
    // KK-1508: sunucu yetkisizliği bazen 404 ile bildirir. Kullanıcıya
    // "sunucu hatası" demek yanlış olur; yetki eksikliği ayrı anlatılır.
    if (yetkisizMi(hata)) {
      return (
        <BosDurum
          baslik="Bu ekran için yetkiniz yok"
          aciklama="Rolünüz bu raporu görüntüleyemiyor. Yetki için okul yönetimine başvurun."
        />
      );
    }
    return (
      <div className="p-4">
        <Uyari tur="hata" baslik="Veriler yüklenemedi">
          <p className="m-0">{hataMetni(hata)}</p>
          {yenile && (
            <p className="m-0 mt-2">
              <Buton tur="ikincil" onClick={yenile}>
                Tekrar dene
              </Buton>
            </p>
          )}
        </Uyari>
      </div>
    );
  }
  if (bos) {
    return (
      <BosDurum
        baslik={bosBaslik}
        {...(bosAciklama === undefined ? {} : { aciklama: bosAciklama })}
        {...(bosEylem === undefined ? {} : { eylem: bosEylem })}
      />
    );
  }
  return <>{children}</>;
}

/** Yazma sonucunu duyuran canlı bölge. */
export function IslemDurumu({
  hata,
  basari,
}: {
  hata?: unknown;
  basari?: string | null;
}) {
  if (hata) {
    return (
      <Uyari tur="hata" baslik="İşlem tamamlanamadı">
        {hataMetni(hata)}
      </Uyari>
    );
  }
  if (basari) return <Uyari tur="onay">{basari}</Uyari>;
  return null;
}

// ---------------------------------------------------------------------------
// Form parçaları — @kantin/ui'da olmayanlar
// ---------------------------------------------------------------------------

export interface MetinAlaniOzellikleri extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  etiket: string;
  ipucu?: string;
  hata?: string;
}

export const MetinAlani = forwardRef<
  HTMLTextAreaElement,
  MetinAlaniOzellikleri
>(function MetinAlani(
  { etiket, ipucu, hata, className = "", id, ...kalan },
  ref,
) {
  const kendiId = useId();
  const alanId = id ?? kendiId;
  const ipucuId = `${alanId}-ipucu`;
  const hataId = `${alanId}-hata`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={alanId} className="text-sm font-satir text-murekkep">
        {etiket}
      </label>
      <textarea
        ref={ref}
        id={alanId}
        aria-invalid={hata ? true : undefined}
        aria-describedby={
          [ipucu ? ipucuId : null, hata ? hataId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
        className={`min-h-16 rounded-md border bg-girdi px-2.5 py-1.5 font-govde text-md text-murekkep transition-colors duration-defter ease-orta placeholder:text-murekkep-3 ${
          hata
            ? "border-hata"
            : "border-cetvel hover:border-cetvel-koyu focus:border-simit"
        } ${className}`}
        {...kalan}
      />
      {ipucu && !hata && (
        <p id={ipucuId} className="m-0 text-xs text-murekkep-3">
          {ipucu}
        </p>
      )}
      {hata && (
        <p id={hataId} className="m-0 text-xs text-hata" role="alert">
          {hata}
        </p>
      )}
    </div>
  );
});

/**
 * Süzgeç girdilerinin ortak kabuğu — tarih, arama ve seçim üçü de aynı
 * 28px'lik gövdeyi paylaşır. Kabuğu üç yerde tekrar yazmak, birinin ötekilerden
 * ayrı düşmesini zaman meselesi yapardı.
 */
const SUZGEC_GIRDI =
  "min-h-kontrol rounded-md border border-cetvel bg-girdi px-2.5 font-govde " +
  "text-md text-murekkep transition-colors duration-defter ease-orta " +
  "hover:border-cetvel-koyu focus:border-simit";

/** Tarih seçici — rapor aralıklarında tekrarlanan alan. */
export function TarihAlani({
  id,
  etiket,
  deger,
  degistir,
  ipucu,
}: {
  id: string;
  etiket: string;
  deger: string;
  degistir: (yeni: string) => void;
  ipucu?: string;
}) {
  const ipucuId = `${id}-ipucu`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-satir text-murekkep">
        {etiket}
      </label>
      <input
        id={id}
        type="date"
        value={deger}
        onChange={(e) => degistir(e.target.value)}
        aria-describedby={ipucu ? ipucuId : undefined}
        className={SUZGEC_GIRDI}
      />
      {ipucu && (
        <p id={ipucuId} className="m-0 text-xs text-murekkep-3">
          {ipucu}
        </p>
      )}
    </div>
  );
}

/** Serbest metin süzgeci — denetim kaydı gibi ekranlarda. */
export function MetinSuzgeci({
  id,
  etiket,
  deger,
  degistir,
  ipucu,
}: {
  id: string;
  etiket: string;
  deger: string;
  degistir: (yeni: string) => void;
  ipucu?: string;
}) {
  const ipucuId = `${id}-ipucu`;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-satir text-murekkep">
        {etiket}
      </label>
      <input
        id={id}
        type="search"
        value={deger}
        onChange={(e) => degistir(e.target.value)}
        aria-describedby={ipucu ? ipucuId : undefined}
        className={SUZGEC_GIRDI}
      />
      {ipucu && (
        <p id={ipucuId} className="m-0 text-xs text-murekkep-3">
          {ipucu}
        </p>
      )}
    </div>
  );
}

/** Sabit seçenekli süzgeç. */
export function SecimSuzgeci({
  id,
  etiket,
  deger,
  degistir,
  secenekler,
}: {
  id: string;
  etiket: string;
  deger: string;
  degistir: (yeni: string) => void;
  secenekler: { deger: string; metin: string }[];
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-satir text-murekkep">
        {etiket}
      </label>
      <select
        id={id}
        value={deger}
        onChange={(e) => degistir(e.target.value)}
        className={SUZGEC_GIRDI}
      >
        {secenekler.map((s) => (
          <option key={s.deger} value={s.deger}>
            {s.metin}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sayfalama ve sekmeler
// ---------------------------------------------------------------------------

export function Sayfalayici({
  sayfa,
  boyut,
  toplam,
  degistir,
}: {
  sayfa: number;
  boyut: number;
  toplam: number;
  degistir: (yeniSayfa: number) => void;
}) {
  if (toplam === 0) return null;
  const sonSayfa = Math.max(1, Math.ceil(toplam / boyut));
  const ilk = (sayfa - 1) * boyut + 1;
  const son = Math.min(sayfa * boyut, toplam);
  return (
    <nav
      className="flex min-h-satir flex-wrap items-center justify-between gap-3 border-t border-cetvel-silik px-3 py-2 text-sm text-murekkep-2 print:hidden"
      aria-label="Sayfalama"
    >
      <p className="m-0" role="status">
        <span className="rakam font-mono">{ilk}</span>–
        <span className="rakam font-mono">{son}</span> / toplam{" "}
        <span className="rakam font-mono">{toplam}</span> kayıt
      </p>
      <div className="flex items-center gap-2">
        <Buton
          tur="ikincil"
          disabled={sayfa <= 1}
          onClick={() => degistir(sayfa - 1)}
        >
          Önceki
        </Buton>
        <span className="rakam font-mono text-murekkep-3">
          {sayfa} / {sonSayfa}
        </span>
        <Buton
          tur="ikincil"
          disabled={sayfa >= sonSayfa}
          onClick={() => degistir(sayfa + 1)}
        >
          Sonraki
        </Buton>
      </div>
    </nav>
  );
}

export function Sekmeler<T extends string>({
  secili,
  sec,
  sekmeler,
  etiket,
}: {
  secili: T;
  sec: (yeni: T) => void;
  sekmeler: { deger: T; metin: string; sayi?: number }[];
  etiket: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={etiket}
      /* Hap grubu (§6.4): aktif hap SEÇİLİ ZEMİN + birincil metin. Alt çizgi
         yok, aksan yok — sekme bir gezinme değil, bir süzgeçtir. */
      className="flex flex-wrap gap-1 print:hidden"
    >
      {sekmeler.map((s) => (
        <button
          key={s.deger}
          role="tab"
          type="button"
          aria-selected={s.deger === secili}
          onClick={() => sec(s.deger)}
          className={`inline-flex min-h-kontrol items-center gap-1.5 rounded-tam px-3 text-sm transition-colors duration-defter ease-orta ${
            s.deger === secili
              ? "bg-kagit-3 font-satir text-murekkep"
              : "text-murekkep-2 hover:bg-kagit-2 hover:text-murekkep"
          }`}
        >
          {s.metin}
          {s.sayi !== undefined && (
            <span className="rakam font-mono text-xs text-murekkep-3">
              {s.sayi}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KK-608 · Dışa aktarım
// ---------------------------------------------------------------------------

/**
 * Excel/CSV indirme butonları.
 *
 * KK-608'in kritik yanı biçim değil, SESSİZ KIRPMA YASAĞIDIR: sunucu satır
 * limitini aşarsa dosyaya bir "UYARI" sayfası ekler ve yanıt başlığında
 * bildirir. Bu bileşen o başlığı okur ve kullanıcıya KALICI bir uyarı
 * gösterir — eksik veriyle karar alınmasın.
 */
export function DisaAktar({
  rapor,
  dosyaKoku,
  sorgu,
  devreDisi = false,
}: {
  rapor: string;
  dosyaKoku: string;
  sorgu: SorguParam;
  devreDisi?: boolean;
}) {
  const [calisan, setCalisan] = useState<"xlsx" | "csv" | null>(null);
  const [uyari, setUyari] = useState<string | null>(null);
  const [hata, setHata] = useState<unknown>(null);

  const indir = async (bicim: "xlsx" | "csv") => {
    setCalisan(bicim);
    setUyari(null);
    setHata(null);
    try {
      const sonuc = await raporIndir(rapor, dosyaKoku, bicim, sorgu);
      if (sonuc.kirpildi) {
        setUyari(
          `Aktarım ${sonuc.limit ?? "satır"} limitine takıldı ve KIRPILDI — indirilen dosya EKSİKTİR. ` +
            "Tarih aralığını daraltın veya nokta süzgeci uygulayıp tekrar alın.",
        );
      }
    } catch (e) {
      setHata(e);
    } finally {
      setCalisan(null);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2 print:hidden">
      <div className="flex flex-wrap gap-2">
        <Buton
          tur="ikincil"
          disabled={devreDisi}
          yukleniyor={calisan === "xlsx"}
          onClick={() => void indir("xlsx")}
        >
          Excel (xlsx)
        </Buton>
        <Buton
          tur="ikincil"
          disabled={devreDisi}
          yukleniyor={calisan === "csv"}
          onClick={() => void indir("csv")}
        >
          CSV
        </Buton>
        <Buton
          tur="sessiz"
          onClick={() => window.print()}
          title="Yazdır veya PDF olarak kaydet"
        >
          Yazdır
        </Buton>
      </div>
      {uyari && (
        <Uyari tur="uyari" baslik="Aktarım kırpıldı" className="max-w-prose">
          {uyari}
        </Uyari>
      )}
      {hata != null && (
        <Uyari tur="hata" baslik="Aktarım alınamadı" className="max-w-prose">
          {hataMetni(hata)}
        </Uyari>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Küçük parçalar
// ---------------------------------------------------------------------------

/** Etiket + değer çifti — kart içi özetlerde. */
export function Olcut({
  etiket,
  children,
}: {
  etiket: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="m-0 text-xs text-murekkep-3">{etiket}</p>
      <div className="mt-0.5 text-md text-murekkep">{children}</div>
    </div>
  );
}

/** Yaşlanma rozetleri — süre büyüdükçe damga sertleşir. */
export function YaslanmaRozeti({
  ms,
  metin,
}: {
  ms: number | null;
  metin: string;
}) {
  const tur =
    ms === null
      ? "notr"
      : ms > 72 * 3600_000
        ? "hata"
        : ms > 24 * 3600_000
          ? "uyari"
          : "notr";
  return <Rozet tur={tur}>{metin} bekliyor</Rozet>;
}
